import { createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  getCoursesOfStudent,
  getDetailCourse,
  getCourseUpcoming,
  getModulesOfCourse,
  getCourses,
  getCoursesDetailOfStudent,
} from './courseThunk';
import { ICourse, ICoursesDetail } from '~/utils/interface/course';
import { IModule } from '~/utils/interface/course';
import { COURSE_ACTIVATION_MESSAGE_KEYS, DEFAULT_PAGE } from '~/utils/constants/common';
import { IBaseErrorData } from '~/utils/interface/base';

interface CourseState {
  mainCourses: ICourse[];
  mainLoading: boolean;
  mainError: boolean;
  courses: ICourse[];
  availableLessons: IModule[];
  scheduledLessons: IModule[];
  coursesDetail: ICoursesDetail;
  currentPage: number;
  hasMore: boolean;
  isLoading: boolean;
  errorMessage?: string;
  // Backend code of the last failed courses/upcoming (e.g. course not active yet)
  errorCode?: number;
}

const initialState: CourseState = {
  mainCourses: [],
  mainLoading: false,
  mainError: false,
  courses: [],
  availableLessons: [],
  scheduledLessons: [],
  coursesDetail: {} as ICoursesDetail,
  currentPage: DEFAULT_PAGE,
  hasMore: false,
  isLoading: false,
};

const courseSlice = createSlice({
  name: 'courseSlice',
  initialState,
  reducers: {
    setNewCourses(state, action) {
      state.courses = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Get Courses Upcoming
    builder
      .addCase(getCourseUpcoming.pending, (state) => {
        state.isLoading = true;
        state.errorMessage = undefined;
        state.errorCode = undefined;
      })
      .addCase(getCourseUpcoming.fulfilled, (state, action) => {
        const coursesData: IModule[] = action.payload.data;
        const today = new Date();
        const futureCourses = coursesData
          .filter((item) => !item.date || (item.date && new Date(item.date) > today))
          .sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return new Date(a.date).getTime() - new Date(b.date).getTime();
          });

        state.scheduledLessons = futureCourses;
        state.availableLessons = coursesData.filter((c) => !futureCourses.includes(c));
        state.isLoading = false;
        state.errorMessage = undefined;
      })
      .addCase(getCourseUpcoming.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMessage = action.error.message;
        state.errorCode = (action.payload as AxiosError<IBaseErrorData> | undefined)?.response?.data?.code;
        if (state.errorCode && COURSE_ACTIVATION_MESSAGE_KEYS[state.errorCode]) {
          state.availableLessons = [];
          state.scheduledLessons = [];
        }
      });

    // Get Detail Courses
    builder
      .addCase(getDetailCourse.pending, (state) => {})
      .addCase(getDetailCourse.fulfilled, (state, action) => {
        state.coursesDetail = action.payload.data;
      })
      .addCase(getDetailCourse.rejected, (state) => {});

    // Get List Courses Of Student
    builder
      .addCase(getCoursesOfStudent.pending, (state) => {})
      .addCase(getCoursesOfStudent.fulfilled, (state) => {})
      .addCase(getCoursesOfStudent.rejected, (state) => {});

    // Get List Modules Of Course
    builder
      .addCase(getModulesOfCourse.pending, (state) => {})
      .addCase(getModulesOfCourse.fulfilled, (state) => {})
      .addCase(getModulesOfCourse.rejected, (state) => {});

    // Get Courses
    builder
      .addCase(getCourses.pending, (state, action) => {
        if (action.meta.arg.isMainPage) {
          state.mainLoading = true;
          state.mainError = false;
        } else {
          state.isLoading = true;
        }
      })
      .addCase(getCourses.fulfilled, (state, action) => {
        if (action.meta.arg.isMainPage) {
          state.mainCourses = action.payload.data.responses;
          state.mainLoading = false;
          state.mainError = false;
        } else {
          state.courses = action.payload.data.responses;
          state.isLoading = false;
          state.currentPage = +action.payload.data.currentPage;
          state.hasMore = action.payload.data.hasMore;
        }
      })
      .addCase(getCourses.rejected, (state, action) => {
        if (action.meta.arg.isMainPage) {
          state.mainLoading = false;
          state.mainError = true;
        } else {
          state.isLoading = false;
        }
      });
    // Get Courses Detail Of Student
    builder
      .addCase(getCoursesDetailOfStudent.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCoursesDetailOfStudent.fulfilled, (state, action) => {
        state.coursesDetail = action.payload.data;
        state.isLoading = false;
      })
      .addCase(getCoursesDetailOfStudent.rejected, (state) => {
        state.isLoading = false;
      });
  },
});

// Actions
export const courseAction = courseSlice.actions;

// Reducer
const courseReducer = courseSlice.reducer;
export default courseReducer;
